import {
  Component,
  DestroyRef,
  forwardRef,
  inject,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { PhoneService } from '@app/@core/services/phone.service';
import { Country } from '@app/@core/models/country.model';

@Component({
  selector: 'ahoy-phone-input',
  templateUrl: './phone-input.component.html',
  styleUrls: ['./phone-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class PhoneInputComponent
  implements ControlValueAccessor, Validator, OnInit
{
  @Input() placeholder = 'Contact #';
  @Input() codeReadonly = false;

  @ViewChild(MatAutocompleteTrigger) autoTrigger: MatAutocompleteTrigger;

  internalForm = new FormGroup({
    phoneCode: new FormControl('', Validators.required),
    phoneNumber: new FormControl('', Validators.required),
  });

  countries: Country[] = [];
  filteredCountries!: Observable<Country[]>;
  touched = false;

  private destroyRef = inject(DestroyRef);
  private onChange: (val: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private phoneService: PhoneService) {}

  ngOnInit(): void {
    this.countries = this.phoneService.getCountries();

    this.filteredCountries =
      this.internalForm.controls.phoneCode.valueChanges.pipe(
        startWith(this.internalForm.controls.phoneCode.value ?? ''),
        map((query) =>
          this.codeReadonly
            ? this.countries
            : this.filterCountries(query ?? ''),
        ),
      );

    this.internalForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const code = this.internalForm.controls.phoneCode.value ?? '';
        const number = this.internalForm.controls.phoneNumber.value ?? '';
        this.onChange(number ? code + number : '');
      });
  }

  private filterCountries(query: string): Country[] {
    const q = query.toLowerCase();
    return this.countries.filter(
      (c) =>
        c.dial_code.toLowerCase().includes(q) ||
        c.name.toLowerCase().includes(q) ||
        c.code.toLowerCase().includes(q),
    );
  }

  // ControlValueAccessor

  writeValue(value: string): void {
    if (value) {
      const parsed = this.phoneService.splitCountryCodeFromPhone(value);
      if (parsed) {
        this.internalForm.setValue(
          {
            phoneCode: parsed.countryCode,
            phoneNumber: String(parsed.nationalNumber),
          },
          { emitEvent: false },
        );
        return;
      }

      // value is a dial code with no number yet (e.g. a default phone code)
      const isDialCodeOnly = this.phoneService
        .getCountries()
        .some((c) => c.dial_code === value);
      if (isDialCodeOnly) {
        this.internalForm.reset(
          { phoneCode: value, phoneNumber: '' },
          { emitEvent: false },
        );
        return;
      }
    }
    this.internalForm.reset(
      { phoneCode: this.phoneService.countryCode, phoneNumber: '' },
      { emitEvent: false },
    );
  }

  registerOnChange(fn: (val: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.internalForm.disable({ emitEvent: false });
    } else {
      this.internalForm.enable({ emitEvent: false });
    }
  }

  // Validator

  validate(_control: AbstractControl): ValidationErrors | null {
    const code = this.internalForm.controls.phoneCode.value ?? '';
    const number = this.internalForm.controls.phoneNumber.value ?? '';
    if (!number) {
      return { required: true };
    }
    const valid = this.phoneService.isValidNumber(code + number);
    return valid ? null : { invalidPhone: true };
  }

  get showRequired(): boolean {
    return this.touched && !this.internalForm.controls.phoneNumber.value;
  }

  get showInvalidPhone(): boolean {
    if (!this.touched) return false;
    const code = this.internalForm.controls.phoneCode.value ?? '';
    const number = this.internalForm.controls.phoneNumber.value ?? '';
    if (!number) return false;
    return !this.phoneService.isValidNumber(code + number);
  }

  phoneErrorMatcher: ErrorStateMatcher = {
    isErrorState: (): boolean => this.showRequired || this.showInvalidPhone,
  };

  displayCountryAndCode = (dialCode: string): string => {
    if (!dialCode) return '';
    const country = this.countries.find((c) => c.dial_code === dialCode);
    return country ? `${country.code} (${dialCode})` : dialCode;
  };

  onBlur(): void {
    this.touched = true;
    this.onTouched();
  }

  private skipClose = false;

  onCodeInteract(): void {
    this.skipClose = true;
  }

  onCodeFocus(): void {
    this.skipClose = false;
    setTimeout(() => {
      if (!this.skipClose) {
        this.autoTrigger?.closePanel();
      }
    }, 0);
  }
}
